import React, { useEffect, useState, useRef } from 'react';

const PROGRESS_DURATION_MS = 18000; // 18s to reach 90%
const PROGRESS_TARGET = 90;
const TICK_MS = 200;
const DONE_DISPLAY_MS = 600;

/**
 * Full-screen grey overlay with centered card: dog image, label, and simulated progress bar.
 * Progress runs 0 → 90% over PROGRESS_DURATION_MS; when isDone, jumps to 100% then calls onClose.
 */
const ScraperLoadingModal = ({ isOpen, isDone, onClose, platformLabel = 'platform' }) => {
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const doneTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setProgress(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (doneTimeoutRef.current) {
        clearTimeout(doneTimeoutRef.current);
        doneTimeoutRef.current = null;
      }
      return;
    }

    setProgress(0);

    if (isDone) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setProgress(100);
      doneTimeoutRef.current = setTimeout(() => {
        onClose?.();
      }, DONE_DISPLAY_MS);
      return () => {
        if (doneTimeoutRef.current) clearTimeout(doneTimeoutRef.current);
      };
    }

    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(PROGRESS_TARGET, (elapsed / PROGRESS_DURATION_MS) * PROGRESS_TARGET);
      setProgress(Math.round(p));
    }, TICK_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isOpen, isDone, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex flex-col items-center space-y-5">
          <img
            src="/loadingdog.png"
            alt="Loading"
            className="h-32 w-32 object-contain sm:h-36 sm:w-36"
          />
          <p className="text-center text-sm font-medium text-slate-700">
            Fetching {platformLabel} stats…
          </p>
          <div className="w-full max-w-xs">
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-[#1B9DA2] transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-2 text-center text-xs text-slate-500">{Math.round(progress)}%</p>
          </div>
          <p className="text-center text-xs text-slate-500">
            This usually takes a few seconds.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScraperLoadingModal;
